import { type Ref, unref } from 'vue';
import L from 'leaflet';
import * as events from '@/events';
import router from '@/router';
import store from '@/store';
import { useToast } from 'vue-toastification';
import { usePersonalLocalStorage } from '@/use/use-personal-local-storage';
import { geofenceCreationDefaultParams } from '@/utils/account-defaults';


// Declare global JSON for TypeScript
declare const JSON: {
  stringify(value: any): string;
  parse(text: string): any;
};

const toast = useToast();

export function useAiActions(map: Ref<L.Map | null>) {
  if (!unref(map)) {
    console.warn('Map is not defined for resize observer');
    return () => {};
  }
  const subscribers: Array<{ unsubscribe: () => void }> = [
    createMapPreserverSubscriber(unref(map)),
    createMapUpdateSubscriber(unref(map)),
    createAddPolygonSubscriber(unref(map)),
    createDevicesFilterSubscriber(),
    createFileSubscriber(),
  ];

  return () => {
    for (const subscriber of subscribers) {
      subscriber.unsubscribe();
    }
  };
}

function createMapUpdateSubscriber(map: L.Map) {
  return events.subscribe(events.names.UI_ACTION_UPDATE_MAP, (parameters) => {
    const center = unref(map).getCenter();
    const zoom = parameters?.zoom ?? unref(map).getZoom();
    if (parameters.latitude) center.lat = parameters.latitude;
    if (parameters.longitude) center.lng = parameters.longitude;
    unref(map).setView(center, zoom);
  });
}

function createMapPreserverSubscriber(map: L.Map) {
  const subscribers: Array<{ unsubscribe: () => void }> = [
    events.subscribe(events.names.UI_ACTION_FILTER_DEVICES, () => {
      localStorage.setItem(
        'previousCenter',
        JSON.stringify([map.getCenter(), map.getZoom()]),
      );
    })
  ];

  return {
    unsubscribe() {
      for (const subscriber of subscribers) {
        subscriber.unsubscribe();
      }
    },
  };
}

function createAddPolygonSubscriber(map: L.Map) {
  let highlight = null;

  return events.subscribe(events.names.UI_ACTION_ADD_POLYGON, (parameters) => {
    if (highlight) {
      highlight.remove();
      highlight = null;
    }
    const path = parameters.path.map((point) => [
      point.latitude,
      point.longitude,
    ]);

    highlight = L.polygon([path], {
      color: '#a20',
      weight: 2,
      opacity: 0.7,
      fillColor: '#fff',
      fillOpacity: 0.7,
    });

    const closeButton = buildRemovePolygonButton(() => {
      highlight.remove();
      highlight = null;
    });
    const createGeofenceButton = buildCreateGeofenceButton(parameters, highlight);
    const popupContent = document.createElement('div');
    popupContent.appendChild(createGeofenceButton);
    popupContent.appendChild(closeButton);

    highlight.bindPopup(popupContent, {
      closeButton: false,
      minWidth: 200,
    });
    highlight.on('mouseover', function (e) { this.openPopup(); });
    highlight.addTo(unref(map));
    map.fitBounds(highlight.getBounds());
  });
}

function buildRemovePolygonButton(callback: () => void) {
  const button = document.createElement('a');
  button.innerText = '×';
  button.className = 'leaflet-popup-close-button cursor-pointer !no-underline';
  button.onclick = callback;
  return button;
}

function buildCreateGeofenceButton(parameters, shape: L.Polygon) {
  const button = document.createElement('a');
  button.innerText = 'Save as a Geofence';
  button.className = 'cursor-pointer';
  button.onclick = (e) => {
    e.preventDefault();
    button.innerText = 'Saving...';
    button.className = 'cursor-wait';
    const params = {
      name: parameters?.name ?? 'New Geofence',
      address: parameters?.address ?? '',
      ...geofenceCreationDefaultParams(store.getters['auth/accountSettings']),
      is_irregular: 1,
      geo_json: JSON.stringify(shape.toGeoJSON().geometry),
    };
    const accountId = store.getters['auth/accountId'];
    const promise = store.dispatch('geofence/store', { accountId, params }) as any;
    if (promise && typeof promise.then === 'function') {
      promise.then((res: any) => {
        events.trigger(events.names.SCOUT_SUGGESTED_GEOFENCE_CREATED, {
          id: res.data.data.id,
          ...params,
        });
        router.push({
          name: 'GeofenceDetailsView',
          params: { id: res.data.data.id },
        });
      }).catch((error: any) => {
        toast.error(
          error?.response?.data?.message ??
            'An error occurred when creating a Geofence.',
        );
      });
    }
  };
  return button;
}

function createDevicesFilterSubscriber() {
  return events.subscribe(events.names.UI_ACTION_FILTER_DEVICES, (parameters) => {
    usePersonalLocalStorage('trackers/specificIds', null).value = JSON.stringify(parameters.device_ids.map((id: any) => +id));
    router.push({ name: 'DeviceListView' });
  });
}

function createFileSubscriber() {
  return events.subscribe(events.names.UI_ACTION_CREATE_FILE, (parameters) => {
    try {
      const { content, filename, mime_type, button_text } = parameters;

      if (!content || !filename || !mime_type) {
        toast.error('Content, filename and MIME type are required');
        return;
      }

      // Create blob with content and specified MIME type
      const blob = new Blob([content], {
        type: mime_type
      });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);

      toast.success(`File "${filename}" downloaded successfully`);

    } catch (error) {
      console.error('Error creating file:', error);
      toast.error('Failed to create file');
    }
  });
}


